import { useState } from 'react';
import { useAppDispatch, useAppSelector, type RootState } from '@/app/store';
import { Send, Loader2 } from 'lucide-react'; // 1. Import Loader2
import { addMessage, type ChatMessage } from '@/features/chat/chatSlice';
import { sendMessage } from '@/app/api';

export default function ChatInput() {
  const dispatch = useAppDispatch();
  const chatId = useAppSelector((state: RootState) => state.chat.chatId);
  const [userText, setUserText] = useState('');
  const [isLoading, setIsLoading] = useState(false); // 2. Add loading state

  const handleSend = async () => {
    // Prevent sending if empty or already loading
    if (!userText.trim() || isLoading) return;

    const tempUserText = userText;
    setUserText('');

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      text: tempUserText,
      sender: 'user',
    };

    dispatch(addMessage(userMessage));

    setIsLoading(true); // 3. Set loading to true
    try {
      const aiMessage = await sendMessage(chatId, tempUserText);
      dispatch(addMessage(aiMessage));
    } catch (error) {
      console.error('Error sending message:', error);
      // Optional: Dispatch an error message to the UI
      dispatch(
        addMessage({
          id: crypto.randomUUID(),
          text: 'No response received from API. You may rephrase your prompt or restart a new session.',
          sender: 'system',
        }),
      );
    } finally {
      setIsLoading(false); // 3. Set loading to false after call completes
    }
  };

  return (
    <div className="fixed bottom-0 w-full max-w-screen-md mx-auto bg-white border-t border-gray-200 p-2 pb-[env(safe-area-inset-bottom, 12px)]">
      <div className="flex gap-2">
        <input
          type="text"
          className="flex-1 border rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          value={userText}
          onChange={(e) => setUserText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isLoading) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Ask about SM Parking and Malls Online database"
          disabled={isLoading} // 4. Disable input when loading
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!userText.trim() || isLoading} // 4. Disable button when loading
          className="flex items-center justify-center w-[76px] px-3 py-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white text-sm disabled:bg-blue-400 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <Loader2 size={16} className="animate-spin" /> // 5. Show spinner
          ) : (
            <>
              Send <Send size={16} />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
